import { useFrame, useThree } from '@react-three/fiber';
import { useRef } from 'react';
import { useShallow } from 'zustand/shallow';

import { applyPoiPointTransform } from 'app/modules/wallforming/components/scenes/objects/pois/utils/poiUtils';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';
import { PRIMARY_COLOR_ACCENT_HEX } from 'shared/constants';

import type { PoiMeshData } from 'app/modules/wallforming/components/scenes/objects/pois/types/poiMeshData';
import type { Mesh } from 'three';

export const PoiPointSelection = () => {
    const pois = usePlacementStore(useShallow((state) => state.pois));
    const hiddenPOIFilterTypes = usePlacementStore(useShallow((state) => state.hiddenPOIFilterTypes));

    const getPoiData = (index: number): PoiMeshData | null => {
        const poiData = pois[index];
        if (!poiData?.poi?.navigationPoint) {
            return null;
        }

        const { x, y, z } = poiData.poi.navigationPoint;
        const isHidden = !poiData.filterType || hiddenPOIFilterTypes.includes(poiData.filterType);
        const scale = isHidden ? 0 : 1.8;

        return { x, y, z, scale, hoverColor: poiData.hoverColor, defaultColor: poiData.defaultColor };
    };

    const selectedPoiIndex = usePlacementStore(useShallow((state) => state.poiSelection.current.index));

    const camera = useThree((state) => state.camera);
    const selectedPoiMeshRef = useRef<Mesh | null>(null);

    const updatePoiSelectionMesh = () => {
        if (!selectedPoiMeshRef.current || selectedPoiIndex === undefined) {
            return;
        }

        const poiData = getPoiData(selectedPoiIndex);
        if (!poiData) {
            return;
        }

        applyPoiPointTransform(selectedPoiMeshRef.current, poiData, camera.zoom);
        selectedPoiMeshRef.current.updateMatrixWorld();
    };

    const handleSelectedPoiMeshRef = (selectedPoiMesh: Mesh | null) => {
        if (selectedPoiMesh) {
            selectedPoiMeshRef.current = selectedPoiMesh;
            updatePoiSelectionMesh();
        }
    };

    useFrame(() => {
        updatePoiSelectionMesh();
    });

    if (selectedPoiIndex === undefined) {
        return null;
    }

    return (
        <mesh ref={handleSelectedPoiMeshRef} renderOrder={50}>
            <sphereGeometry args={[1, 16, 16]} />
            <meshBasicMaterial color={PRIMARY_COLOR_ACCENT_HEX} depthTest={false} opacity={0.2} transparent />
        </mesh>
    );
};
